import { useEffect, useState } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import type { TaskTypeDirType, TaskTypeStyleType } from '@econtractor/types';
import { useSettings } from 'context/SettingsContext';
import { useTheme } from 'context/ThemeContext';
import { selectStylesDark, selectStylesLigth } from 'utils/helpers/structs/selectStyles';
import { tDirName } from 'utils/helpers/i18n/tDirName';
import InputLabel from 'components/others/InputLabel';
import TaskTypeChip from './TaskTypeChip';

export interface TaskTypeOption {
    value: number;
    label: string;
    icon?: string | null;
    colors?: TaskTypeStyleType | null;
}

interface TaskTypeSelectProps {
    value?: number | null;
    onChange: (option: TaskTypeOption | null) => void;
    disabled?: boolean;
    hideLabel?: boolean;
}

const TaskTypeSelect = ({
    value = null,
    onChange,
    disabled = false,
    hideLabel = false,
}: TaskTypeSelectProps) => {
    const { t } = useTranslation(['Tasks']);
    const { darkTheme } = useTheme();
    const { localSettings } = useSettings();
    const [selectTheme, setSelectTheme] = useState(darkTheme ? selectStylesDark : selectStylesLigth);
    const [options, setOptions] = useState<TaskTypeOption[]>([]);

    useEffect(() => {
        setSelectTheme(darkTheme ? selectStylesDark : selectStylesLigth);
    }, [darkTheme]);

    useEffect(() => {
        setOptions(
            (localSettings?.DIRS?.TaskType as TaskTypeDirType[] | undefined)?.map((tt) => ({
                value: tt.ID,
                label: tDirName(t, tt.i18nKey, tt.taskTypeName),
                icon: tt.taskTypeIcon || null,
                colors: tt.taslTypeJson || null,
            })) || [],
        );
    }, [localSettings?.DIRS?.TaskType, t]);

    useEffect(() => {
        if (value != null || options.length === 0) return;
        onChange(options[options.length - 1]);
    }, [value, options, onChange]);

    return (
        <InputLabel title={hideLabel ? '' : t('labelType')} rightElement={null}>
            <Select<TaskTypeOption>
                options={options}
                isDisabled={disabled}
                isClearable={false}
                className="w-100 task-type-select"
                styles={{
                    ...selectTheme,
                    control: (base, state) => ({
                        ...base,
                        ...(selectTheme.control ? selectTheme.control(base, state) : {}),
                        minHeight: 42,
                    }),
                    option: (base, state) => ({
                        ...base,
                        ...(selectTheme.option ? selectTheme.option(base, state) : {}),
                        backgroundColor: state.isFocused
                            ? (darkTheme ? 'rgba(255,255,255,0.06)' : '#F8F9FA')
                            : 'transparent',
                        cursor: 'pointer',
                        paddingTop: 8,
                        paddingBottom: 8,
                    }),
                    singleValue: (base) => ({
                        ...base,
                        ...(selectTheme.singleValue ? selectTheme.singleValue(base) : {}),
                        color: 'inherit',
                    }),
                }}
                formatOptionLabel={(option) => (
                    <TaskTypeChip name={option.label} icon={option.icon} colors={option.colors} />
                )}
                value={options.find((opt) => opt.value == value) || null}
                onChange={onChange}
            />
        </InputLabel>
    );
};

export default TaskTypeSelect;
